import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useAuth } from '../components/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  FiGrid, FiUsers, FiDollarSign, FiSearch, FiChevronLeft, FiChevronRight, FiEye, FiCheckCircle, FiTrash
} from 'react-icons/fi';
import './Admin.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const formatCurrency = (value) =>
  `R$ ${(Number(value || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (dateString) =>
  dateString ? new Date(dateString).toLocaleDateString('pt-BR') : '—';

/* ============== helpers de mês ============== */
const monthsShort = ['jan.','fev.','mar.','abr.','mai.','jun.','jul.','ago.','set.','out.','nov.','dez.'];
function labelFromYYYYMM(ym) {
  if (!ym) return '';
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return ym || '';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}
const clampYear = (y) =>
  Math.min(9999, Math.max(1970, Number.isFinite(+y) ? +y : new Date().getFullYear()));

/* ============== helper CPF ============== */
const formatCPF = (v) => {
  const s = String(v || '').replace(/\D/g, '').slice(0, 11);
  if (s.length !== 11) return v || '—';
  return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/* ============== UI bits ============== */
const StatusBadge = ({ status }) => {
  const s = String(status || '').toUpperCase();
  const cls =
    s === 'PAID' ? 'paid' :
    s === 'PENDING' ? 'pending' :
    (s === 'CANCELLED' || s === 'CANCELED') ? 'cancelled' : 'neutral';
  const label =
    s === 'PAID' ? 'Pago' :
    s === 'PENDING' ? 'Pendente' :
    (s === 'CANCELLED' || s === 'CANCELED') ? 'Cancelado' : (status || '—');
  return <span className={`adm-badge ${cls}`}>{label}</span>;
};

const KpiCard = ({ title, value, icon }) => (
  <div className="adm-kpi">
    <div className="adm-kpi__icon">{React.createElement(icon, { size: 22 })}</div>
    <div className="adm-kpi__meta">
      <p className="adm-kpi__title">{title}</p>
      <p className="adm-kpi__value">{value}</p>
    </div>
  </div>
);

/* ============== Toggle (Pendentes/Pagas) ============== */
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      className={`adm-switch ${checked ? 'is-on' : ''}`}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      aria-label={checked ? 'Mostrar pagas' : 'Mostrar pendentes'}
      title={checked ? 'Pagas' : 'Pendentes'}
    >
      <span className="adm-switch__thumb" />
    </button>
  );
}

/* ============== Month Picker custom ============== */
function MonthPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [y, m] = String(value || '').split('-').map(Number);
  const [year, setYear] = useState(() => clampYear(y || new Date().getFullYear()));
  const fieldRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => setYear(clampYear(y || new Date().getFullYear())), [y]);

  useEffect(() => {
    function onDocClick(e) {
      if (!open) return;
      if (fieldRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const setThisMonth = () => {
    const now = new Date();
    onChange(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    setYear(now.getFullYear());
    setOpen(false);
  };

  return (
    <div className="adm-month" ref={fieldRef}>
      <button
        type="button"
        className="adm-month__field"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {labelFromYYYYMM(value)}
        <span className="adm-month__icon" aria-hidden>📅</span>
      </button>

      {open && (
        <div className="adm-month__popover" ref={popRef} role="dialog" aria-label="Selecionar mês">
          <div className="adm-month__year">
            <button type="button" onClick={() => setYear(y => y - 1)} aria-label="Ano anterior">‹</button>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(clampYear(e.target.value))}
              className="adm-month__year-input"
              aria-label="Ano"
            />
            <button type="button" onClick={() => setYear(y => y + 1)} aria-label="Próximo ano">›</button>
          </div>

          <div className="adm-month__grid" role="listbox">
            {monthsShort.map((mm, idx) => {
              const monthNum = idx + 1;
              const isSel = (year === y) && (monthNum === m);
              return (
                <button
                  key={mm}
                  className={`adm-month__cell ${isSel ? 'is-selected' : ''}`}
                  onClick={() => { onChange(`${year}-${String(monthNum).padStart(2, '0')}`); setOpen(false); }}
                  role="option"
                  aria-selected={isSel}
                >
                  {mm}
                </button>
              );
            })}
          </div>

          <div className="adm-month__foot">
            <button type="button" className="adm-link" onClick={() => { onChange(''); setOpen(false); }}>
              Limpar
            </button>
            <button type="button" className="adm-link" onClick={setThisMonth}>
              Este mês
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== Views ============== */
const DashboardView = ({ overview }) => (
  <div className="adm-stack">
    <div className="adm-grid4">
      <KpiCard title="Usuários Ativos" value={overview?.usersCount ?? '—'} icon={FiUsers} />
      <KpiCard title="Membros na Rede" value={overview?.totalNetworkSize?.toLocaleString('pt-BR') ?? '—'} icon={FiUsers} />
      <KpiCard title="Comissão Total" value={formatCurrency(overview?.totalCommissionAll)} icon={FiDollarSign} />
      <KpiCard title="Comissão Paga" value={formatCurrency(overview?.totalCommissionPaid)} icon={FiCheckCircle} />
    </div>

    <div className="adm-card">
      <h3 className="adm-card__title">Top 5 Usuários por Comissão</h3>
      <div className="adm-chart">
        <ResponsiveContainer>
          <BarChart data={overview?.topUsersByCommission} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid stroke="var(--adm-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid var(--adm-border)', boxShadow: 'var(--adm-shadow)' }}
              formatter={(v) => formatCurrency(v)}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
            <Bar dataKey="total" name="Comissão Total" radius={[4, 4, 0, 0]} fill="var(--adm-blue)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

const UsersView = ({ authHeader }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(15);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async (p = 1, search = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/users`, {
        headers: authHeader,
        params: { page: p, limit, q: search },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('users:', e);
    } finally {
      setLoading(false);
    }
  }, [authHeader, limit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => { loadUsers(page, q); }, [page, q, loadUsers]);

  return (
    <div className="adm-card">
      <div className="adm-toolbar">
        <div className="adm-input-icon">
          <FiSearch className="adm-input-icon__icon" />
          <input
            type="text"
            className="adm-input"
            placeholder="Buscar por nome ou e-mail…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
          />
        </div>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Membros</th>
              <th>Cliques</th>
              <th>Comissão Total</th>
              <th>Taxa Plataforma (%)</th>
              <th>Data de Entrada</th>
              <th className="adm-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="adm-empty">Carregando usuários...</td></tr>}
            {!loading && rows.map(r => (
              <tr key={r._id}>
                <td>
                  <div className="adm-user">
                    {r.picture && (
                      <img
                        className="adm-user__avatar"
                        src={r.picture}
                        alt={r.name}
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    )}
                    <div className="adm-user__meta">
                      <div className="adm-user__name">{r.name}</div>
                      <div className="adm-user__email">{r.email}</div>
                    </div>
                    {r.role === 'admin' && <span className="adm-badge indigo">Admin</span>}
                  </div>
                </td>
                <td className="adm-strong">{r.groupParticipantsCount ?? 0}</td>
                <td className="adm-strong">{r.totalClicks ?? 0}</td>
                <td className="adm-strong green">{formatCurrency(r.totalCommission)}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      defaultValue={r.platformFeePercent ?? 30}
                      onBlur={async (e) => {
                        const v = Number(e.target.value);
                        if (!Number.isFinite(v) || v < 0 || v > 100) {
                          alert('Percentual inválido. Use 0 a 100.');
                          e.target.value = r.platformFeePercent ?? 30;
                          return;
                        }
                        try {
                          await axios.put(
                            `${API_BASE}/api/admin/users/${r._id}/platform-fee`,
                            { percent: v },
                            { headers: authHeader }
                          );
                        } catch (err) {
                          console.error('platform-fee save:', err);
                          alert('Falha ao salvar a taxa.');
                          e.target.value = r.platformFeePercent ?? 30;
                        }
                      }}
                      className="adm-input"
                      style={{ width: 70, padding: '.3rem .5rem' }}
                    />
                  </div>
                </td>
                <td>{formatDate(r.createdAt)}</td>
                <td className="adm-right">
                  <button
                    onClick={() => window.open(`${window.location.origin}/dashboard?as=${r._id}`, '_blank', 'noopener,noreferrer')}
                    className="adm-btn adm-btn--icon"
                    title="Ver dashboard do usuário"
                  >
                    <FiEye size={18} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan="7" className="adm-empty">Nenhum usuário encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="adm-pagination">
        <span> Total de <b>{total}</b> usuários</span>
        <div className="adm-pagination__ctrls">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="adm-btn">
            <FiChevronLeft /> Anterior
          </button>
          <span className="adm-page">Página {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= total || loading} className="adm-btn">
            Próxima <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

const InvoicesView = ({ authHeader }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isGenerating, setIsGenerating] = useState(false);

  // modal PIX/CPF + QR Code
  const [pixDlg, setPixDlg] = useState(null); // {name,email,cpf,cpfRaw,pixKey,amount,status,...}
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [keyChoice, setKeyChoice] = useState('pix'); // 'pix' | 'cpf'

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/invoices`, {
        headers: authHeader,
        params: { status: statusFilter, page, limit: pageSize }
      });
      setInvoices(data.rows || []);
      setTotal(data.total || 0);
      if (data.limit) setPageSize(data.limit);
    } catch (e) {
      console.error('invoices:', e);
    } finally {
      setLoading(false);
    }
  }, [authHeader, statusFilter, page, pageSize]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const handleGenerateInvoices = async () => {
    if (!window.confirm(`Gerar faturas para ${selectedMonth}?`)) return;
    setIsGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/admin/invoices/generate`,
        { month: selectedMonth },
        { headers: authHeader }
      );
      alert(data.message || 'Faturas processadas.');
      setStatusFilter('PENDING'); setPage(1);
      loadInvoices();
    } catch (e) {
      console.error("Erro ao gerar faturas:", e);
      alert(e.response?.data?.message || "Falha ao gerar faturas.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMarkAsPaid = useCallback(async (invoiceId) => {
    if (!window.confirm("Marcar esta fatura como PAGA?")) return;
    try {
      await axios.put(
        `${API_BASE}/api/admin/invoices/${invoiceId}/mark-as-paid`,
        {},
        { headers: authHeader }
      );
      // Se removemos o último item da página, voltar uma página e recarregar
      if (invoices.length <= 1 && page > 1) {
        setPage(p => Math.max(1, p - 1));
      } else {
        loadInvoices();
      }
    } catch (e) {
      console.error("Erro ao marcar como paga:", e);
      alert("Falha ao atualizar a fatura.");
    }
  }, [authHeader, invoices.length, page, loadInvoices]);

  const handleDeleteInvoice = useCallback(async (invoiceId) => {
    if (!window.confirm("Excluir esta fatura? Essa ação não pode ser desfeita.")) return;
    try {
      await axios.delete(
        `${API_BASE}/api/admin/invoices/${invoiceId}`,
        { headers: authHeader }
      );
      if (invoices.length <= 1 && page > 1) {
        setPage(p => Math.max(1, p - 1));
      } else {
        loadInvoices();
      }
    } catch (e) {
      console.error("Erro ao excluir fatura:", e);
      alert(e.response?.data?.message || "Falha ao excluir a fatura.");
    }
  }, [authHeader, invoices.length, page, loadInvoices]);

  const isPaid = statusFilter === 'PAID';
  const hasMore = page * pageSize < total;
  const colSpan = statusFilter === 'PENDING' ? 6 : 5;

  const openPixModal = (inv) => {
    const cpfRaw = inv.userCpf ? String(inv.userCpf).replace(/\D/g, '').slice(0, 11) : '';
    setPixDlg({
      name: inv.userName || '—',
      email: inv.userEmail || '—',
      cpf: inv.userCpf ? formatCPF(inv.userCpf) : '—',
      cpfRaw,
      pixKey: inv.userPixKey || '—',
      amount: inv.amount,
      status: inv.status,
      month: inv.month
    });
    setKeyChoice('pix');
    setQrDataUrl(null);
  };

  const closePixModal = () => {
    setPixDlg(null);
    setQrDataUrl(null);
  };

  // Gerar QR Code PIX via API (gpix - formato correto) para faturas PENDENTES
  useEffect(() => {
    if (!pixDlg || pixDlg.status !== 'PENDING') return;
    const amount = Number(pixDlg.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;

    const key = keyChoice === 'cpf' ? pixDlg.cpfRaw : (pixDlg.pixKey && pixDlg.pixKey !== '—' ? pixDlg.pixKey : null);
    if (!key) return;
    if (keyChoice === 'cpf' && key.length !== 11) return;

    let cancelled = false;
    axios.post(
      `${API_BASE}/api/admin/invoices/pix-payload`,
      {
        key,
        name: pixDlg.name !== '—' ? pixDlg.name : 'RECEBEDOR',
        amount,
        txid: pixDlg.month || '***'
      },
      { headers: authHeader }
    )
      .then(({ data }) => {
        if (!cancelled && data.brcode) {
          return QRCode.toDataURL(data.brcode, { width: 256, margin: 2 });
        }
      })
      .then((dataUrl) => {
        if (!cancelled && dataUrl) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => { cancelled = true; };
  }, [pixDlg, keyChoice, authHeader]);

  const copyPix = async () => {
    if (!pixDlg?.pixKey || pixDlg.pixKey === '—') return;
    try {
      await navigator.clipboard.writeText(String(pixDlg.pixKey));
      setPixDlg((s) => ({ ...s, copied: true }));
      setTimeout(() => setPixDlg((s) => (s ? { ...s, copied: false } : s)), 1200);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = String(pixDlg.pixKey);
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
      setPixDlg((s) => ({ ...s, copied: true }));
      setTimeout(() => setPixDlg((s) => (s ? { ...s, copied: false } : s)), 1200);
    }
  };

  return (
    <div className="adm-stack">
      {/* CARD COM OVERFLOW LIBERADO (cirúrgico) */}
      <div className="adm-card adm-card--flow">
        <h3 className="adm-card__title">Gerar Faturas Mensais</h3>

        <div className="adm-row adm-row--wrap">
          <MonthPicker value={selectedMonth} onChange={(v) => setSelectedMonth(v || '')} />
          <button
            onClick={handleGenerateInvoices}
            disabled={isGenerating || !selectedMonth}
            className="adm-btn adm-btn--primary adm-btn--generate"
          >
            {isGenerating ? 'Gerando...' : `Gerar Faturas de ${selectedMonth}`}
          </button>
        </div>

        <p className="adm-help">
          Procura comissões validadas no mês selecionado que ainda não foram faturadas e cria os registros.
        </p>
      </div>

      <div className="adm-card">
        <div className="adm-card__head">
          <h3 className="adm-card__title">Faturas Geradas</h3>

          <div className="adm-switch-wrap">
            <span className={`adm-switch-label ${!isPaid ? 'is-active' : ''}`}>Pendentes</span>
            <ToggleSwitch
              checked={isPaid}
              onChange={(next) => { setStatusFilter(next ? 'PAID' : 'PENDING'); setPage(1); }}
            />
            <span className={`adm-switch-label ${isPaid ? 'is-active' : ''}`}>Pagas</span>
          </div>
        </div>

        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Período</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Data Criação</th>
                {statusFilter === 'PENDING' && <th className="adm-center">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={colSpan} className="adm-empty">Carregando faturas...</td></tr>}
              {!loading && invoices.map(inv => (
                <tr key={inv._id} className="adm-row-click" onClick={() => openPixModal(inv)}>
                  <td>
                    <div className="adm-user__meta">
                      <div className="adm-user__name">{inv.userName}</div>
                      <div className="adm-user__email">{inv.userEmail}</div>
                    </div>
                  </td>
                  <td>{inv.month}</td>
                  <td className="adm-strong green">{formatCurrency(inv.amount)}</td>
                  <td><StatusBadge status={inv.status} /></td>
                  <td>{formatDate(inv.createdAt)}</td>
                  {statusFilter === 'PENDING' && (
                    <td className="adm-center" onClick={(e) => e.stopPropagation()}>
                      <div className="adm-actions">
                        <button onClick={() => handleMarkAsPaid(inv._id)} className="adm-btn adm-btn--success">
                          Marcar como Paga
                        </button>
                        <button
                          onClick={() => handleDeleteInvoice(inv._id)}
                          className="adm-btn adm-btn--danger"
                          title="Excluir fatura"
                        >
                          <FiTrash size={16} /> Excluir
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!loading && invoices.length === 0 && <tr><td colSpan={colSpan} className="adm-empty">Nenhuma fatura encontrada.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="adm-pagination">
          <span> Total de <b>{total}</b> faturas</span>
          <div className="adm-pagination__ctrls">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="adm-btn"
            >
              <FiChevronLeft /> Anterior
            </button>
            <span className="adm-page">Página {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore || loading}
              className="adm-btn"
            >
              Próxima <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL PIX/CPF + QR Code */}
      {pixDlg && (
        <div className="adm-modal" role="dialog" aria-modal="true">
          <div className="adm-modal__overlay" onClick={closePixModal} />
          <div className="adm-modal__content">
            <h3 className="adm-modal__title">
              {pixDlg.status === 'PENDING' && qrDataUrl ? 'Pagar via PIX' : 'Dados para Pagamento'}
            </h3>
            {pixDlg.status === 'PENDING' && (pixDlg.pixKey !== '—' || pixDlg.cpfRaw?.length === 11) && (
              <div className="adm-modal__key-choice">
                <span className="adm-modal__key-label">Usar como chave PIX:</span>
                <div className="adm-modal__key-options">
                  <label className="adm-modal__key-opt">
                    <input
                      type="radio"
                      name="pixKeyChoice"
                      checked={keyChoice === 'pix'}
                      onChange={() => { setKeyChoice('pix'); setQrDataUrl(null); }}
                    />
                    Chave PIX
                  </label>
                  <label className="adm-modal__key-opt">
                    <input
                      type="radio"
                      name="pixKeyChoice"
                      checked={keyChoice === 'cpf'}
                      onChange={() => { setKeyChoice('cpf'); setQrDataUrl(null); }}
                      disabled={!pixDlg.cpfRaw || pixDlg.cpfRaw.length !== 11}
                    />
                    CPF
                  </label>
                </div>
              </div>
            )}
            {pixDlg.status === 'PENDING' && (qrDataUrl ? (
              <div className="adm-modal__qr">
                <img src={qrDataUrl} alt="QR Code PIX" className="adm-modal__qr-img" />
                <p className="adm-modal__qr-valor">{formatCurrency(pixDlg.amount)}</p>
                <p className="adm-modal__qr-hint">Escaneie com o app do seu banco para pagar</p>
              </div>
            ) : (keyChoice === 'pix' && pixDlg.pixKey && pixDlg.pixKey !== '—') || (keyChoice === 'cpf' && pixDlg.cpfRaw?.length === 11) ? (
              <div className="adm-modal__qr adm-modal__qr--loading">Gerando QR Code…</div>
            ) : null)}
            <div className="adm-modal__grid">
              <div className="adm-field">
                <label>Nome</label>
                <div className="adm-field__value">{pixDlg.name}</div>
              </div>
              <div className="adm-field">
                <label>E-mail</label>
                <div className="adm-field__value">{pixDlg.email}</div>
              </div>
              <div className="adm-field">
                <label>CPF</label>
                <div className="adm-field__value">{pixDlg.cpf || '—'}</div>
              </div>
              <div className="adm-field">
                <label>Chave PIX</label>
                <div className="adm-field__value adm-field__row">
                  <span className="adm-mono">{pixDlg.pixKey || '—'}</span>
                  {pixDlg.pixKey && pixDlg.pixKey !== '—' && (
                    <button className="adm-btn adm-copy" onClick={copyPix}>
                      {pixDlg.copied ? 'Copiado!' : 'Copiar'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="adm-modal__actions">
              <button className="adm-btn" onClick={closePixModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============== Página /admin ============== */
export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const isAdmin = user?.role === 'admin';
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${user?.token}` }), [user?.token]);

  const loadOverview = useCallback(async () => {
    if (!authHeader.Authorization) return;
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/overview`, { headers: authHeader });
      setOverview(data);
    } catch (e) { console.error('overview:', e); }
  }, [authHeader]);

  useEffect(() => { if (isAdmin) loadOverview(); }, [isAdmin, loadOverview]);

  if (!isAdmin) {
    return (
      <div className="adm-guard">
        <div className="adm-card adm-guard__card">
          <h2 className="adm-guard__title">Acesso Negado</h2>
          <p className="adm-guard__desc">Você precisa ser um administrador para ver esta página.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: FiGrid },
    { id: 'users', name: 'Usuários', icon: FiUsers },
    { id: 'invoices', name: 'Faturamento', icon: FiDollarSign },
  ];

  return (
    <div className="adm-root">
      <div className="adm-container">
        <header className="adm-header">
          <div>
            <h1 className="adm-title">Painel Administrativo</h1>
            <p className="adm-subtitle">Visão geral do sistema, usuários e faturamento.</p>
          </div>
        </header>

        <div className="adm-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`adm-tab ${activeTab === tab.id ? 'is-active' : ''}`}
              title={tab.name}
            >
              {React.createElement(tab.icon, { size: 16 })}
              <span>{tab.name}</span>
            </button>
          ))}
        </div>

        <main className="adm-main">
          {activeTab === 'dashboard' && <DashboardView overview={overview} />}
          {activeTab === 'users' && <UsersView authHeader={authHeader} />}
          {activeTab === 'invoices' && <InvoicesView authHeader={authHeader} />}
        </main>
      </div>
    </div>
  );
}
